import React, { useRef, useState } from 'react'
import styled from 'styled-components/macro'
import { useSelector } from 'react-redux'
import { message } from 'antd'
import { API } from 'src/utils/interceptor'
import OrdersIcon from 'src/components/icons/OrdersIcon'
import InvoicesIcon from 'src/components/icons/InvoicesIcon'
import PriceFilesIcon from 'src/components/icons/PriceFilesIcon'
import ConnectionIcon from 'src/components/icons/ConnectionIcon'
import SyncIcon from 'src/components/icons/SyncIcon'
import ReceiveIcon from 'src/components/icons/ReceiveIcon'

const Integration = () => {
  const formRef = useRef(null)
  const account = useSelector(state => state.auth.account)

  // Form state
  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [companyName, setCompanyName] = useState(
    account?.supplier[0]?.title || '',
  )
  const [phoneNumber, setPhoneNumber] = useState('')
  const [currentSystem, setCurrentSystem] = useState('')
  const [note, setNote] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState({})

  const handleScrollToForm = () => {
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  const validateEmail = email => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return re.test(email)
  }

  const validateForm = () => {
    const newErrors = {}

    if (!fullName.trim()) {
      newErrors.fullName = 'Full Name is required'
    }

    if (!email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!validateEmail(email)) {
      newErrors.email = 'Invalid email format'
    }

    if (!companyName.trim()) {
      newErrors.companyName = 'Company / Supplier Name is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async e => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)

    try {
      await API.post('api2', 'suppliers/integration/register', {
        body: {
          fullName: fullName.trim(),
          email: email.trim(),
          companyName: companyName.trim(),
          phoneNumber: phoneNumber.trim() || undefined,
          currentSystem: currentSystem.trim() || undefined,
          note: note.trim() || undefined,
          supplierId: account?.supplier[0]?._id,
        },
      })

      message.success(
        "Thanks — we'll contact you within 2 business days to discuss your integration.",
      )

      // Reset form
      setFullName('')
      setEmail('')
      setCompanyName(account?.supplier[0]?.title || '')
      setPhoneNumber('')
      setCurrentSystem('')
      setNote('')
      setErrors({})
    } catch (error) {
      console.error('Form submission error:', error)
      message.error('Failed to submit. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <PageWrapper>
      <HeroCard>
        <HeroHeadline>
          Free Integration — Orders Flow Directly Into Your System
        </HeroHeadline>
        <HeroSubheadline>
          Orders appear instantly in your software. Invoices and price files
          sync automatically into OMS. Fully automated. Zero hassle.
        </HeroSubheadline>
        <StressFreeLine>
          We handle everything — your IT provider just shares a few details and
          we do the rest.
        </StressFreeLine>
        <TrustLine>
          Hundreds of suppliers already use OMS to automate their workflows.
        </TrustLine>
        <PrimaryCTA type='button' onClick={handleScrollToForm}>
          Register interest
        </PrimaryCTA>
      </HeroCard>

      <SectionCard>
        <SectionTitle>What We Integrate</SectionTitle>
        <CardGrid>
          <IntegrateCard>
            <IntegrateIconWrapper>
              <OrdersIcon size='32px' />
            </IntegrateIconWrapper>
            <ValueTitle>Orders → Your System</ValueTitle>
            <ValueText>
              Every order placed on OMS appears instantly in your existing
              software. No manual entry, no delays, no mistakes.
            </ValueText>
          </IntegrateCard>
          <IntegrateCard>
            <IntegrateIconWrapper>
              <InvoicesIcon size='32px' />
            </IntegrateIconWrapper>
            <ValueTitle>Invoices → OMS</ValueTitle>
            <ValueText>
              Your invoices sync automatically to OMS. Your customers see
              accurate pricing immediately, with no manual uploads.
            </ValueText>
          </IntegrateCard>
          <IntegrateCard>
            <IntegrateIconWrapper>
              <PriceFilesIcon size='32px' />
            </IntegrateIconWrapper>
            <ValueTitle>Price Files → OMS</ValueTitle>
            <ValueText>
              Your latest prices flow into OMS automatically. Customers always
              see current pricing without you lifting a finger.
            </ValueText>
          </IntegrateCard>
        </CardGrid>
      </SectionCard>

      <SectionCard>
        <SectionTitle>Supplier Benefits</SectionTitle>
        <CardGrid>
          <ValueCard>
            <ValueTitle>Orders appear in your existing software</ValueTitle>
            <ValueText>
              Every order placed on OMS drops straight into the system your team
              already uses — no switching tools, no extra steps.
            </ValueText>
          </ValueCard>
          <ValueCard>
            <ValueTitle>Fewer admin tasks</ValueTitle>
            <ValueText>
              Automation removes manual data entry and eliminates mistakes,
              saving your team hours every week.
            </ValueText>
          </ValueCard>
          <ValueCard>
            <ValueTitle>Less manual work</ValueTitle>
            <ValueText>
              Your team processes orders exactly as they do today — just without
              all the manual input.
            </ValueText>
          </ValueCard>
          <ValueCard>
            <ValueTitle>No new training</ValueTitle>
            <ValueText>
              Nothing changes for your staff; orders land where they already
              work, in a format they already understand.
            </ValueText>
          </ValueCard>
          <ValueCard>
            <ValueTitle>Better customer experience</ValueTitle>
            <ValueText>
              Your customers get faster service, accurate pricing, and fewer
              mistakes — all because their data is always up to date.
            </ValueText>
          </ValueCard>
          <ValueCard>
            <ValueTitle>Integration can be absolutely free</ValueTitle>
            <ValueText>
              Integration can be absolutely free where your system provider
              supports automated connections — we handle the setup to make
              everything effortless for you.
            </ValueText>
          </ValueCard>
        </CardGrid>
      </SectionCard>

      <SectionCard>
        <SectionTitle>Customer Benefits</SectionTitle>
        <CardGrid>
          <ValueCard>
            <ValueTitle>Accurate pricing</ValueTitle>
            <ValueText>
              Customers always see your current prices in OMS, with no outdated
              files or confusion.
            </ValueText>
          </ValueCard>
          <ValueCard>
            <ValueTitle>Automated invoices</ValueTitle>
            <ValueText>
              Invoices sync automatically into OMS, giving customers clean,
              accurate billing without extra work.
            </ValueText>
          </ValueCard>
          <ValueCard>
            <ValueTitle>Fewer mistakes</ValueTitle>
            <ValueText>
              Automated syncing keeps order and invoice data clean, reducing
              errors and preventing back-and-forth corrections.
            </ValueText>
          </ValueCard>
          <ValueCard>
            <ValueTitle>Faster workflows</ValueTitle>
            <ValueText>
              Orders move through their process automatically, letting customers
              place, track, and confirm orders faster.
            </ValueText>
          </ValueCard>
          <ValueCard>
            <ValueTitle>Everything in one place</ValueTitle>
            <ValueText>
              Customers manage all their suppliers in a single app, instead of
              juggling multiple websites and ordering systems.
            </ValueText>
          </ValueCard>
          <ValueCard>
            <ValueTitle>No missed orders</ValueTitle>
            <ValueText>
              Orders flow instantly into your system, so customers never have to
              worry about lost messages or missed voicemails.
            </ValueText>
          </ValueCard>
        </CardGrid>
      </SectionCard>

      <SectionCard>
        <SectionTitle>Why Not Use Your Own Ordering App?</SectionTitle>
        <WhyNotText>
          Because chefs don&apos;t want ten different apps. They won&apos;t
          download a new app for every supplier — they want one place, one
          workflow, and zero clutter.
        </WhyNotText>
        <WhyNotText>
          OMS gives venues a single system to manage orders from all of their
          suppliers. It&apos;s where chefs already work every day — and it plugs
          directly into the back-of-house tools used by larger restaurants,
          hotel groups, and multi-site operators.
        </WhyNotText>
        <WhyNotText>
          When you use your own app, you&apos;re asking chefs to switch tools,
          remember passwords, and learn a whole new flow. Most won&apos;t
          bother. When you integrate with OMS, you meet them where they already
          are — and become the easiest supplier to order from.
        </WhyNotText>
        <WhyNotText $bold>And the easiest supplier always wins.</WhyNotText>
      </SectionCard>

      <SectionCard>
        <SectionTitle>How Integration Works</SectionTitle>
        <StepsGrid>
          <StepCard>
            <StepIconWrapper>
              <ConnectionIcon size='32px' />
            </StepIconWrapper>
            <StepTitle>Step 1 — We connect your system</StepTitle>
            <StepText>
              Our team handles the technical setup. We connect OMS to your
              existing software — no work required from you.
            </StepText>
          </StepCard>
          <StepCard>
            <StepIconWrapper>
              <SyncIcon size='32px' />
            </StepIconWrapper>
            <StepTitle>Step 2 — Everything syncs automatically</StepTitle>
            <StepText>
              Orders flow into your system. Invoices and price files sync to
              OMS. It all happens automatically, 24/7.
            </StepText>
          </StepCard>
          <StepCard>
            <StepIconWrapper>
              <ReceiveIcon size='32px' />
            </StepIconWrapper>
            <StepTitle>
              Step 3 — You receive orders as usual — just without the manual
              entry
            </StepTitle>
            <StepText>
              Orders appear in your software instantly. Your team processes them
              exactly as they always have — just without the manual entry.
            </StepText>
          </StepCard>
        </StepsGrid>
      </SectionCard>

      <SectionCard>
        <SectionTitle>Why OMS Offers Free Integration</SectionTitle>
        <TransparencyText>
          OMS is free for ordering because suppliers like you invite your
          customers.
        </TransparencyText>
        <TransparencyText>
          We offer free integration because it makes you more likely to invite
          customers — which gives us more opportunities to sell paid modules to
          those customers.
        </TransparencyText>
        <TransparencyText>
          When your venues upgrade to features like Menu Costing, Recipe
          Builder, or Stock Takes, that revenue keeps OMS free — and helps us
          keep integration free for you.
        </TransparencyText>
        <TransparencyText>
          This is a win–win–win situation: you get automation, your customers
          get better service, and we get more opportunities to grow.
        </TransparencyText>
        <ProtectiveWording>
          Integration can be absolutely free where supported by your system
          provider. Some suppliers use a managed partner connection that
          includes additional monitoring and support.
        </ProtectiveWording>
      </SectionCard>

      <SectionCard ref={formRef}>
        <SectionTitle>Get Started with Integration</SectionTitle>
        <FormContainer onSubmit={handleSubmit}>
          <FormField>
            <InputLabel htmlFor='integration-full-name'>Full Name</InputLabel>
            <TextInput
              id='integration-full-name'
              type='text'
              placeholder='Your full name'
              value={fullName}
              onChange={e => setFullName(e.target.value)}
            />
            {errors.fullName && <ErrorMessage>{errors.fullName}</ErrorMessage>}
          </FormField>
          <FormField>
            <InputLabel htmlFor='integration-email'>Email</InputLabel>
            <TextInput
              id='integration-email'
              type='email'
              placeholder='your.email@example.com'
              value={email}
              onChange={e => setEmail(e.target.value)}
            />
            {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
          </FormField>
          <FormField>
            <InputLabel htmlFor='integration-company-name'>
              Company / Supplier Name
            </InputLabel>
            <TextInput
              id='integration-company-name'
              type='text'
              placeholder='Your company name'
              value={companyName}
              onChange={e => setCompanyName(e.target.value)}
            />
            {errors.companyName && (
              <ErrorMessage>{errors.companyName}</ErrorMessage>
            )}
          </FormField>
          <FormField>
            <InputLabel htmlFor='integration-phone'>
              Phone Number (optional)
            </InputLabel>
            <TextInput
              id='integration-phone'
              type='tel'
              placeholder='Your phone number'
              value={phoneNumber}
              onChange={e => setPhoneNumber(e.target.value)}
            />
          </FormField>
          <FormField>
            <InputLabel htmlFor='integration-current-system'>
              Current System / Software (optional)
            </InputLabel>
            <TextInput
              id='integration-current-system'
              type='text'
              placeholder='What system do you currently use?'
              value={currentSystem}
              onChange={e => setCurrentSystem(e.target.value)}
            />
          </FormField>
          <FormField>
            <InputLabel htmlFor='integration-note'>Optional note</InputLabel>
            <TextAreaInput
              id='integration-note'
              rows={4}
              placeholder='Any additional information...'
              value={note}
              onChange={e => setNote(e.target.value)}
            />
          </FormField>
          <FormSubmitButton type='submit' disabled={isSubmitting}>
            {isSubmitting ? 'Submitting...' : 'Register interest'}
          </FormSubmitButton>
        </FormContainer>
      </SectionCard>
    </PageWrapper>
  )
}

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sp-space-xxl);

  @media (max-width: 1024px) {
    gap: var(--sp-space-xl);
  }

  @media (max-width: 768px) {
    gap: var(--sp-space-lg);
  }
`

const SectionCard = styled.section`
  background: var(--sp-color-surface);
  border-radius: var(--sp-radius-card);
  padding: var(--sp-space-xl);
  box-shadow: var(--sp-shadow-card);
  border: 1px solid var(--sp-color-border);

  @media (max-width: 768px) {
    padding: var(--sp-space-md);
  }
`

const HeroCard = styled(SectionCard)`
  text-align: left;
`

const HeroHeadline = styled.h1`
  margin: 0 0 var(--sp-space-md);
  color: var(--sp-color-text);
  font-size: 32px;
  font-weight: 700;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`

const HeroSubheadline = styled.p`
  margin: 0 0 var(--sp-space-md);
  color: var(--sp-color-text);
  font-size: 18px;
  line-height: 1.5;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`

const StressFreeLine = styled.p`
  margin: var(--sp-space-lg) 0 var(--sp-space-sm);
  color: var(--sp-color-text);
  font-size: 18px;
  font-weight: 700;
  line-height: 1.5;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`

const TrustLine = styled.p`
  margin: 0 0 var(--sp-space-lg);
  color: var(--sp-color-muted);
  font-size: 15px;
  line-height: 1.5;
`

const PrimaryCTA = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 var(--sp-space-xl);
  min-height: 48px;
  border-radius: var(--sp-radius-control);
  border: none;
  background: var(--sp-color-primary);
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.9;
  }

  &:focus {
    outline: 2px solid var(--sp-color-primary);
    outline-offset: 2px;
  }
`

const SectionTitle = styled.h2`
  margin: 0 0 var(--sp-space-lg);
  color: var(--sp-color-text);
  font-size: 24px;
  font-weight: 700;

  @media (max-width: 768px) {
    font-size: 20px;
  }
`

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: var(--sp-space-md);
  margin-top: var(--sp-space-md);

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const IntegrateCard = styled.div`
  padding: var(--sp-space-md);
  border-radius: var(--sp-radius-card);
  background: rgba(14, 165, 233, 0.04);
  border: 1px solid var(--sp-color-border);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`

const IntegrateIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: var(--sp-color-primary);
  color: #fff;
  margin-bottom: var(--sp-space-md);

  svg {
    color: #fff;
  }

  svg path,
  svg rect,
  svg circle {
    stroke: #fff;
    fill: none;
  }

  svg path[fill='currentColor'] {
    fill: #fff;
    stroke: none;
  }
`

const ValueCard = styled.div`
  padding: var(--sp-space-md);
  border-radius: var(--sp-radius-card);
  background: rgba(14, 165, 233, 0.04);
  border: 1px solid var(--sp-color-border);
`

const ValueTitle = styled.h3`
  margin: 0 0 var(--sp-space-sm);
  color: var(--sp-color-text);
  font-size: 18px;
  font-weight: 700;
`

const ValueText = styled.p`
  margin: 0;
  color: var(--sp-color-muted);
  font-size: 15px;
  line-height: 1.6;
`

const WhyNotText = styled.p`
  margin: ${({ $bold }) =>
    $bold ? 'var(--sp-space-md) 0 0' : '0 0 var(--sp-space-md)'};
  color: var(--sp-color-text);
  font-size: 16px;
  line-height: 1.7;
  font-weight: ${({ $bold }) => ($bold ? '700' : '400')};
`

const StepsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: var(--sp-space-lg);
  margin-top: var(--sp-space-md);

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: var(--sp-space-md);
  }
`

const StepCard = styled.div`
  padding: var(--sp-space-lg);
  border-radius: var(--sp-radius-card);
  background: rgba(14, 165, 233, 0.04);
  border: 1px solid var(--sp-color-border);
`

const StepIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: var(--sp-color-primary);
  color: #fff;
  margin-bottom: var(--sp-space-md);

  svg {
    color: #fff;
  }

  svg path {
    stroke: #fff;
    fill: #fff;
  }
`

const StepTitle = styled.h3`
  margin: 0 0 var(--sp-space-sm);
  color: var(--sp-color-text);
  font-size: 18px;
  font-weight: 700;
`

const StepText = styled.p`
  margin: 0 0 var(--sp-space-sm);
  color: var(--sp-color-muted);
  font-size: 15px;
  line-height: 1.6;
`

const TransparencyText = styled.p`
  margin: var(--sp-space-md) 0 0;
  color: var(--sp-color-text);
  font-size: 16px;
  line-height: 1.7;

  &:first-of-type {
    margin-top: 0;
  }
`

const ProtectiveWording = styled.p`
  margin: var(--sp-space-lg) 0 0;
  color: var(--sp-color-muted);
  font-size: 14px;
  line-height: 1.6;
  font-style: italic;
`

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: var(--sp-space-md);
  margin-top: var(--sp-space-md);
  max-width: 600px;
  width: 100%;

  @media (max-width: 768px) {
    max-width: 100%;
  }
`

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sp-space-xs);
`

const InputLabel = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: var(--sp-color-text);
`

const ErrorMessage = styled.span`
  font-size: 13px;
  color: #ef4444;
  margin-top: var(--sp-space-xs);
`

const TextInput = styled.input`
  padding: var(--sp-space-sm) var(--sp-space-md);
  border: 1px solid var(--sp-color-border);
  border-radius: var(--sp-radius-control);
  font-size: 15px;
  background: var(--sp-color-surface);
  color: var(--sp-color-text);
  transition: border-color 0.2s ease;

  &:hover:not(:disabled) {
    border-color: var(--sp-color-primary);
  }

  &:focus {
    outline: 2px solid var(--sp-color-primary);
    outline-offset: 2px;
    border-color: var(--sp-color-primary);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const TextAreaInput = styled.textarea`
  padding: var(--sp-space-sm) var(--sp-space-md);
  border: 1px solid var(--sp-color-border);
  border-radius: var(--sp-radius-control);
  font-size: 15px;
  background: var(--sp-color-surface);
  color: var(--sp-color-text);
  font-family: inherit;
  resize: vertical;
  transition: border-color 0.2s ease;

  &:hover:not(:disabled) {
    border-color: var(--sp-color-primary);
  }

  &:focus {
    outline: 2px solid var(--sp-color-primary);
    outline-offset: 2px;
    border-color: var(--sp-color-primary);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const FormSubmitButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 var(--sp-space-xl);
  min-height: 48px;
  border-radius: var(--sp-radius-control);
  border: none;
  background: var(--sp-color-primary);
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s ease;
  margin-top: var(--sp-space-sm);

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:focus {
    outline: 2px solid var(--sp-color-primary);
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

export default Integration
